// tslint:disable:object-literal-sort-keys object-shorthand-properties-first

/**
 * @file
 * Processing and formatting for Historical Stats API and Table rows
 *
 * Important: Historical stats (ie /v1/data/angler-stats/28709) returns a single
 * object from the backend. This has to be converted to ross of objects to be
 * used in the Table component.
 */
import * as React from 'react';

// Libraries
import { formatMoney, prettifyOunces } from '../../util/util';

// Types
import { AnglerId } from '../../types';
import { IAnglerHistoricalApiProcessed } from './types';

/**
 * Line up the full words of the stat with the returned values
 */
interface ILookup {
  words: string;
  hasLink: boolean;
  category: string;
}
const lookup: ILookup[] = [
  {
    category: 'all',
    hasLink: true,
    words: 'Total Tournaments',
  },
  {
    category: 'classic',
    hasLink: true,
    words: 'Classic Appearances',
  },
  {
    category: 'classicwins',
    hasLink: true,
    words: 'Classic Wins',
  },
  {
    category: 'money',
    hasLink: true,
    words: 'Time in Money',
  },
  {
    category: 'first',
    hasLink: true,
    words: '1st Place Finishes',
  },
  {
    category: 'second',
    hasLink: true,
    words: '2nd Place Finishes',
  },
  {
    category: 'third',
    hasLink: true,
    words: '3rd Place Finishes',
  },
  {
    category: 'top10',
    hasLink: true,
    words: 'Top 10 Finishes',
  },
  {
    category: 'top20',
    hasLink: true,
    words: 'Top 20 Finishes',
  },
  {
    category: 'top30',
    hasLink: true,
    words: 'Top 30 Finishes',
  },
  {
    category: 'weight',
    hasLink: false,
    words: 'Total Weight',
  },
  {
    category: 'cash',
    hasLink: false,
    words: 'Career Winnings',
  },
];

export const apiParser = (apiData: any): IAnglerHistoricalApiProcessed[] => {
  if (Array.isArray(apiData)) {
    apiData = apiData[0];
  }
  // Return array of objects that Table needs
  return Object.keys(apiData).map(category => ({
    stat: category,
    value: apiData[category],
  }));
};

export function buildHistoricalStatsTableRow(
  this: { anglerId: AnglerId },
  { stat, value }: IAnglerHistoricalApiProcessed
) {
  const { words, category, hasLink } = lookup.find(
    lookupItem => lookupItem.category === stat
  ) || { words: '', category: '', hasLink: false };

  // Specific processing
  let valueFormatted = '';
  switch (category) {
    case 'cash':
      valueFormatted = formatMoney(value);
      break;
    case 'weight':
      valueFormatted = prettifyOunces(value);
      break;
    default:
      valueFormatted = value.toString();
  }

  const link = hasLink ? (
    <a href={`/angler/stats/${this.anglerId}/${category}`}>{valueFormatted}</a>
  ) : (
    <span>{valueFormatted}</span>
  );
  if (!words) {
    return {};
  }

  return {
    stat: <span>{words}</span>,
    link,
  };
}
